import {
  Drawer, Box, Typography, IconButton, Button, Checkbox, Chip, Divider,
  CircularProgress, Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import BlockIcon from '@mui/icons-material/Block';
import GavelIcon from '@mui/icons-material/Gavel';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import GroupsIcon from '@mui/icons-material/Groups';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { colors } from '../theme/designTheme';
import { useCaseDecision, useUpsertDecision, useUpdateAction } from '../hooks/useDecision';

const fmtMoney = (n) => (n == null ? '—' : `$${Number(n).toLocaleString()}`);

const OutcomeTile = ({ label, value, sub, color, Icon }) => (
  <Box sx={{
    flex: '1 1 160px', minWidth: 160,
    p: 1.5, borderRadius: 1.5,
    bgcolor: colors.surfaceElevated,
    border: `1px solid ${color || colors.borderSubtle}`,
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
      {Icon && <Icon sx={{ color: color || colors.textMuted, fontSize: 14 }} />}
      <Typography sx={{ color: colors.textMuted, fontFamily: 'Lexend', fontSize: '9px', fontWeight: 700, letterSpacing: '0.1em' }}>
        {label.toUpperCase()}
      </Typography>
    </Box>
    <Typography sx={{ color: color || colors.textPrimary, fontFamily: 'Lexend', fontSize: '18px', fontWeight: 800, lineHeight: 1.1 }}>
      {value}
    </Typography>
    {sub && (
      <Typography sx={{ color: colors.textSecondary, fontFamily: 'Lexend', fontSize: '10px', mt: 0.3 }}>
        {sub}
      </Typography>
    )}
  </Box>
);

const statusMeta = {
  completed:      { color: colors.low,      Icon: CheckCircleIcon,    label: 'DONE' },
  pending:        { color: colors.textMuted, Icon: HourglassEmptyIcon, label: 'PENDING' },
  blocked:        { color: colors.critical, Icon: BlockIcon,          label: 'BLOCKED' },
  not_applicable: { color: colors.textMuted, Icon: BlockIcon,          label: 'N/A' },
};

const ActionRow = ({ action, onToggle, busy }) => {
  const meta = statusMeta[action.status] || statusMeta.pending;
  const Icon = meta.Icon;
  const done = action.status === 'completed';
  return (
    <Box sx={{
      display: 'flex', alignItems: 'flex-start', gap: 1.25,
      py: 1, borderBottom: `1px solid ${colors.borderSubtle}`,
      '&:last-of-type': { borderBottom: 'none' },
    }}>
      <Checkbox
        checked={done} disabled={busy}
        onChange={() => onToggle(action)}
        sx={{ color: colors.textMuted, p: 0.25, mt: 0.25, '&.Mui-checked': { color: colors.low } }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Typography sx={{
            color: done ? colors.textMuted : colors.textPrimary,
            fontFamily: 'Lexend', fontSize: '12px', fontWeight: 600,
            textDecoration: done ? 'line-through' : 'none',
          }}>
            {action.title}
          </Typography>
          {action.assignee && (
            <Chip
              size="small" label={action.assignee}
              sx={{
                bgcolor: colors.surfaceElevated, color: colors.textSecondary,
                border: `1px solid ${colors.borderSubtle}`, borderRadius: '3px',
                fontFamily: 'Lexend', fontSize: '9px', fontWeight: 600, height: 18,
              }}
            />
          )}
          {action.deadline && (
            <Chip
              size="small" label={`Due ${action.deadline}`}
              sx={{
                bgcolor: colors.highBg, color: colors.high,
                border: `1px solid ${colors.highBorder}`, borderRadius: '3px',
                fontFamily: 'Lexend', fontSize: '9px', fontWeight: 700, height: 18,
              }}
            />
          )}
        </Box>
        {action.description && (
          <Typography sx={{ color: colors.textSecondary, fontFamily: 'Lexend', fontSize: '11px', mt: 0.4, lineHeight: 1.45 }}>
            {action.description}
          </Typography>
        )}
      </Box>
      <Tooltip title={meta.label}>
        <Icon sx={{ color: meta.color, fontSize: 16, mt: 0.5 }} />
      </Tooltip>
    </Box>
  );
};

const DecisionPanel = ({ open, onClose, caseNumber }) => {
  const { data, isLoading } = useCaseDecision(caseNumber, open);
  const upsert = useUpsertDecision(caseNumber);
  const updateAction = useUpdateAction(caseNumber);

  const toggle = (action) => {
    updateAction.mutate({
      actionId: action.id,
      status: action.status === 'completed' ? 'pending' : 'completed',
    });
  };

  const HIDDEN_ACTION_TYPES_BY_CASE = {
    '5023': ['customer_notification', 'valuer_review'],
  };
  const hiddenTypes = HIDDEN_ACTION_TYPES_BY_CASE[String(caseNumber)] || [];
  const visibleActions = (data?.actions || [])
    .filter((a) => !hiddenTypes.includes(a.action_type))
    .map((a) => ({ ...a, status: 'pending' }));
  const immediate = visibleActions.filter((a) => a.category === 'immediate');
  const downstream = visibleActions.filter((a) => a.category === 'downstream');

  return (
    <Drawer
      anchor="right" open={open} onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: { xs: '100%', md: 720 },
            bgcolor: colors.surface,
            borderLeft: `1px solid ${colors.border}`,
            display: 'flex', flexDirection: 'column',
          },
        },
      }}
    >
      {/* Header */}
      <Box sx={{
        px: 3, py: 2, borderBottom: `1px solid ${colors.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
      }}>
        <Box>
          <Typography sx={{ color: colors.textPrimary, fontFamily: 'Lexend', fontWeight: 700, fontSize: '14px' }}>
            Decision &amp; Downstream Actions
          </Typography>
          <Typography sx={{ color: colors.textMuted, fontFamily: 'Lexend', fontSize: '10px', mt: 0.3, letterSpacing: '0.06em' }}>
            REF-{caseNumber}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: colors.textMuted, '&:hover': { color: colors.accent } }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 3, py: 2 }}>
        {isLoading || !data ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} sx={{ color: colors.accent }} />
          </Box>
        ) : (
          <>
            {/* Decision summary strip */}
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3 }}>
              <OutcomeTile
                label="Investigator" Icon={GavelIcon}
                value={data.investigator || '—'}
              />
              <OutcomeTile
                label="Agent Rec" Icon={TrendingUpIcon}
                value={data.agent_recommendation ? data.agent_recommendation.split(' ')[0] : '—'}
                sub={data.agent_recommendation}
              />
              <OutcomeTile
                label="Evidence Pack" Icon={ReceiptLongIcon}
                value={`${data.evidence_attachment_count}`}
                sub="attachments"
              />
            </Box>

            {/* AI-generated next-steps attribution */}
            {(immediate.length > 0 || downstream.length > 0) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
                <AutoAwesomeIcon sx={{ color: colors.accent, fontSize: 14 }} />
                <Typography sx={{
                  color: colors.accent, fontFamily: 'Lexend', fontSize: '11px',
                  fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                }}>
                  Next steps generated by AI
                </Typography>
              </Box>
            )}

            {/* Immediate actions */}
            {immediate.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ color: colors.critical, fontFamily: 'Lexend', fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', mb: 1 }}>
                  IMMEDIATE (BEFORE DEADLINE)
                </Typography>
                <Box sx={{
                  bgcolor: colors.surfaceElevated, border: `1px solid ${colors.borderSubtle}`,
                  borderRadius: 1.5, px: 1.5,
                }}>
                  {immediate.map((a) => (
                    <ActionRow key={a.id} action={a} onToggle={toggle} busy={updateAction.isPending} />
                  ))}
                </Box>
              </Box>
            )}

            {/* Downstream actions */}
            {downstream.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ color: colors.high, fontFamily: 'Lexend', fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', mb: 1 }}>
                  DOWNSTREAM (POST-DECISION)
                </Typography>
                <Box sx={{
                  bgcolor: colors.surfaceElevated, border: `1px solid ${colors.borderSubtle}`,
                  borderRadius: 1.5, px: 1.5,
                }}>
                  {downstream.map((a) => (
                    <ActionRow key={a.id} action={a} onToggle={toggle} busy={updateAction.isPending} />
                  ))}
                </Box>
              </Box>
            )}

            <Divider sx={{ borderColor: colors.borderSubtle, my: 2 }} />

            {/* Outcome tracking */}
            <Typography sx={{ color: colors.textPrimary, fontFamily: 'Lexend', fontSize: '12px', fontWeight: 700, mb: 1.25, letterSpacing: '0.05em' }}>
              Outcome Tracking
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
              <OutcomeTile label="Funding" value={data.settlement_blocked} sub="blocked status" />
              <OutcomeTile label="SMR" value={data.smr_filed} sub="financial crime" />
              <OutcomeTile label="Facility" value={data.facility_declined} sub="credit" />
              <OutcomeTile
                label="Loss Prevented" Icon={TrendingUpIcon}
                value={fmtMoney(data.loss_prevented)}
                color={colors.low}
              />
              <OutcomeTile
                label="Model Feedback"
                value={(data.model_feedback || 'unknown').replace('_', ' ')}
                color={data.model_feedback === 'true_positive' ? colors.low : colors.textMuted}
              />
              <OutcomeTile
                label="Cluster" Icon={GroupsIcon}
                value={data.cluster_identified_count != null
                  ? `${data.cluster_identified_count} of ${data.cluster_estimated_count ?? '?'}`
                  : '—'}
                sub="entities identified"
              />
            </Box>
          </>
        )}
      </Box>

      {/* Footer: finalize actions */}
      {data && data.investigator_decision === 'pending' && (
        <Box sx={{
          px: 3, py: 2, borderTop: `1px solid ${colors.border}`,
          display: 'flex', gap: 1, justifyContent: 'flex-end', flexShrink: 0,
        }}>
          <Button
            size="small"
            onClick={() => upsert.mutate({ investigator_decision: 'override' })}
            disabled={upsert.isPending}
            sx={{
              color: colors.critical, border: `1px solid ${colors.criticalBorder}`,
              fontFamily: 'Lexend', fontSize: '11px', fontWeight: 700, textTransform: 'none', px: 2,
              '&:hover': { bgcolor: colors.criticalBg },
            }}
          >
            Override Agent
          </Button>
          <Button
            size="small"
            onClick={() => upsert.mutate({ investigator_decision: 'concur' })}
            disabled={upsert.isPending}
            sx={{
              bgcolor: colors.accent, color: colors.bg,
              fontFamily: 'Lexend', fontSize: '11px', fontWeight: 700, textTransform: 'none', px: 2,
              '&:hover': { bgcolor: '#00b8d9' },
            }}
          >
            Concur &amp; Execute
          </Button>
        </Box>
      )}
    </Drawer>
  );
};

export default DecisionPanel;
